/**
 * @budgeting101/api-client — Shared domain types
 *
 * These mirror the backend's Prisma/Zod schemas. The canonical source of
 * truth is the backend OpenAPI spec; this file is a typed hand-written
 * reflection until codegen is wired up.
 *
 * Rule: amounts are always { amountMinor: string, currency: string }.
 * No floats. No division by 10 here — that belongs in AmountText.
 */

import { z } from 'zod';

// ── Money ──────────────────────────────────────────────────────────────────

export const MoneySchema = z.object({
  amountMinor: z.string(),
  currency: z.string().length(3),
});
export type Money = z.infer<typeof MoneySchema>;

// ── Common enums ───────────────────────────────────────────────────────────

export const TxKindSchema = z.enum([
  'income',
  'expense',
  'transfer',
  'withdrawal',
  'customer_invoice',
  'customer_payment',
  'customer_prepayment',
  'customer_prepayment_apply',
  'customer_leave_cash',
  'customer_collect_cash',
]);
export type TxKind = z.infer<typeof TxKindSchema>;

export const TxStatusSchema = z.enum(['draft', 'pending', 'approved', 'posted', 'rejected']);
export type TxStatus = z.infer<typeof TxStatusSchema>;

export const AccountTypeSchema = z.enum(['bank', 'wallet', 'customer', 'category', 'system']);
export type AccountType = z.infer<typeof AccountTypeSchema>;

// ── Pagination ─────────────────────────────────────────────────────────────

export const PaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    data: z.array(itemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    hasMore: z.boolean(),
  });

export type Paginated<T> = {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

// ── Auth ───────────────────────────────────────────────────────────────────

export const LoginResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  expiresAt: z.string().datetime().optional(),
  companyId: z.string().optional(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
    role: z.string().optional(),
  }),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ── Account ────────────────────────────────────────────────────────────────

export const AccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: AccountTypeSchema,
  currency: z.string().length(3),
  balanceMinor: z.string(),
  isArchived: z.boolean(),
  createdAt: z.string().datetime(),
});
export type Account = z.infer<typeof AccountSchema>;

// ── Transaction ────────────────────────────────────────────────────────────

export const TransactionSchema = z.object({
  id: z.string(),
  kind: TxKindSchema,
  status: TxStatusSchema,
  memo: z.string(),
  amountMinor: z.string(),
  currency: z.string().length(3),
  date: z.string().datetime(),
  fromAccountId: z.string().nullable(),
  toAccountId: z.string().nullable(),
  categoryId: z.string().nullable(),
  customerId: z.string().nullable(),
  memberId: z.string().nullable(),
  attachmentUrls: z.array(z.string().url()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Transaction = z.infer<typeof TransactionSchema>;

// ── Approval ───────────────────────────────────────────────────────────────

export const ApprovalSchema = z.object({
  id: z.string(),
  transactionId: z.string(),
  requesterId: z.string(),
  requesterName: z.string(),
  description: z.string(),
  amountMinor: z.string(),
  currency: z.string().length(3),
  status: z.enum(['pending', 'approved', 'rejected']),
  decidedAt: z.string().datetime().nullable(),
  decidedById: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  requestedAt: z.string().datetime(),
  evidenceUrls: z.array(z.string().url()),
  budgetImpact: z.object({
    budgetId: z.string(),
    budgetLabel: z.string(),
    beforePct: z.number(),
    afterPct: z.number(),
  }).nullable(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

// ── Budget ─────────────────────────────────────────────────────────────────

export const BudgetSchema = z.object({
  id: z.string(),
  label: z.string(),
  limitMinor: z.string(),
  postedMinor: z.string(),
  pendingMinor: z.string(),
  currency: z.string().length(3),
  policy: z.enum(['warn', 'block']),
  period: z.object({
    type: z.enum(['monthly', 'quarterly', 'annual', 'custom']),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
  }),
  scope: z.object({
    type: z.enum(['category', 'member', 'account', 'company']),
    id: z.string().nullable(),
    label: z.string(),
  }),
});
export type Budget = z.infer<typeof BudgetSchema>;

// ── Member ─────────────────────────────────────────────────────────────────

export const MemberSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  roleId: z.string(),
  roleName: z.string(),
  isActive: z.boolean(),
  joinedAt: z.string().datetime(),
});
export type Member = z.infer<typeof MemberSchema>;

// ── Customer (Party) ───────────────────────────────────────────────────────

export const CustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email().nullable(),
  receivableMinor: z.string(),
  custodyMinor: z.string(),
  prepaidMinor: z.string(),
  currency: z.string().length(3),
  createdAt: z.string().datetime(),
});
export type Customer = z.infer<typeof CustomerSchema>;

// ── API error ──────────────────────────────────────────────────────────────

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
  /** Server request correlation ID, shown to users for support. */
  referenceId?: string;
}
